/**
 * Input - 범용 텍스트 입력 컴포넌트
 *
 * @param label - 상단 라벨
 * @param error - 에러 메시지 (표시 시 입력 필드에 에러 스타일 적용)
 * @param leftIcon / rightIcon - 입력 필드 양쪽에 배치할 아이콘
 * @param prefix / suffix - 입력 필드 양쪽에 고정 텍스트/요소
 */
'use client';

import React, { forwardRef } from 'react';
import { AlertTriangle } from 'lucide-react';
import styles from './Input.module.css';

export interface InputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'size' | 'prefix'
> {
  label?: string;
  error?: string;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  inputType?: 'text' | 'copy' | 'currency' | 'date' | 'url' | 'phone' | 'number';
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      leftIcon,
      rightIcon,
      size = 'md',
      prefix,
      suffix,
      inputType,
      className = '',
      id,
      ...props
    },
    ref,
  ) => {
    const inputId = id || (label ? `input-${label.replace(/\s+/g, '-').toLowerCase()}` : undefined);
    const hasError = Boolean(error);

    const sizeClass =
      size !== 'md' ? styles[`size${size.charAt(0).toUpperCase()}${size.slice(1)}`] : '';
    const inputTypeClass = inputType
      ? styles[`type${inputType.charAt(0).toUpperCase()}${inputType.slice(1)}`]
      : '';

    return (
      <div className={styles.formGroup}>
        {label && (
          <label htmlFor={inputId} className={styles.label}>
            {label}
          </label>
        )}
        <div
          className={[styles.inputWrapper, hasError ? styles.wrapperError : '']
            .filter(Boolean)
            .join(' ')}
        >
          {prefix && <span className={styles.prefix}>{prefix}</span>}
          {leftIcon && <span className={styles.leftIcon}>{leftIcon}</span>}
          <input
            ref={ref}
            id={inputId}
            className={[
              styles.input,
              hasError ? styles.inputError : '',
              leftIcon ? styles.hasLeftIcon : '',
              rightIcon ? styles.hasRightIcon : '',
              sizeClass,
              inputTypeClass,
              prefix ? styles.inputWithPrefix : '',
              suffix ? styles.inputWithSuffix : '',
              className,
            ]
              .filter(Boolean)
              .join(' ')}
            aria-invalid={hasError}
            aria-describedby={
              inputId
                ? hasError
                  ? `${inputId}-error`
                  : helperText
                    ? `${inputId}-helper`
                    : undefined
                : undefined
            }
            {...props}
          />
          {rightIcon && <span className={styles.rightIcon}>{rightIcon}</span>}
          {suffix && <span className={styles.suffix}>{suffix}</span>}
        </div>
        {hasError && (
          <p className={styles.errorText} id={`${inputId}-error`} role="alert">
            <AlertTriangle size={14} className={styles.errorIcon} />
            {error}
          </p>
        )}
        {!hasError && helperText && (
          <p className={styles.helperText} id={`${inputId}-helper`}>
            {helperText}
          </p>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';

export default Input;
